import { useMemo } from 'react';
import type { Campaign, CampaignStatus } from '@/lib/types';
import { CampaignCard, type CampaignActions } from './CampaignCard';

interface CampaignsViewProps extends CampaignActions {
  campaigns: Campaign[];
}

const SECTION_ORDER: { key: CampaignStatus; label: string; description: string }[] = [
  { key: 'active', label: 'Activas', description: 'En curso · midiendo respuestas' },
  { key: 'paused', label: 'Pausadas', description: 'Detenidas · listas para reanudar' },
  { key: 'draft', label: 'Borradores', description: 'Listas para revisar antes de enviar' },
  { key: 'completed', label: 'Completadas', description: 'Cerradas · ver resultados' },
];

export function CampaignsView({ campaigns, ...actions }: CampaignsViewProps) {
  const grouped = useMemo(() => {
    const byStatus: Record<CampaignStatus, Campaign[]> = {
      active: [],
      paused: [],
      draft: [],
      completed: [],
    };
    for (const c of campaigns) byStatus[c.status].push(c);
    for (const k of Object.keys(byStatus) as CampaignStatus[]) {
      byStatus[k].sort((a, b) =>
        (b.sentAt ?? b.createdAt).localeCompare(a.sentAt ?? a.createdAt)
      );
    }
    return byStatus;
  }, [campaigns]);

  const totals = {
    active: grouped.active.length,
    paused: grouped.paused.length,
    draft: grouped.draft.length,
    completed: grouped.completed.length,
  };

  const aggregate = useMemo(() => {
    const t = { delivered: 0, opened: 0, responded: 0, scheduled: 0, recipients: 0 };
    for (const c of grouped.active) {
      t.recipients += c.recipientCount;
      t.delivered += c.metrics.delivered;
      t.opened += c.metrics.opened;
      t.responded += c.metrics.responded;
      t.scheduled += c.metrics.scheduled;
    }
    return t;
  }, [grouped.active]);

  return (
    <>
      {(totals.active + totals.paused) > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-6 max-[880px]:grid-cols-2">
          <SummaryCell label="Activas" value={totals.active} />
          <SummaryCell label="Destinatarios" value={aggregate.recipients} />
          <SummaryCell
            label="Respondieron"
            value={aggregate.responded}
            subtotal={aggregate.delivered}
          />
          <SummaryCell
            label="Agendaron consulta"
            value={aggregate.scheduled}
            subtotal={aggregate.delivered}
            accent
          />
        </div>
      )}

      {SECTION_ORDER.map(({ key, label, description }) => {
        const items = grouped[key];
        if (items.length === 0) return null;
        return (
          <section key={key} className="mb-2">
            <h3 className="section-title">
              <span>
                {label} ({items.length})
                <span className="font-medium normal-case tracking-normal text-muted ml-1">
                  · {description}
                </span>
              </span>
            </h3>
            <div className="flex flex-col gap-2 mb-4">
              {items.map((c) => (
                <CampaignCard key={c.id} campaign={c} {...actions} />
              ))}
            </div>
          </section>
        );
      })}

      {totals.active + totals.paused + totals.draft + totals.completed === 0 && (
        <div className="glass-panel py-6 text-center text-muted text-[13px]">
          Aún no hay campañas. Selecciona una oportunidad y crea la primera desde "Crear campaña".
        </div>
      )}
    </>
  );
}

interface SummaryCellProps {
  label: string;
  value: number;
  subtotal?: number;
  accent?: boolean;
}

function SummaryCell({ label, value, subtotal, accent }: SummaryCellProps) {
  const p = subtotal && subtotal > 0 ? Math.round((value / subtotal) * 100) : null;
  return (
    <div
      className={`bg-glass backdrop-blur-xl backdrop-saturate-150 border rounded-lg shadow-card p-3.5 px-4 ${
        accent ? 'border-ok' : 'border-line-glass'
      }`}
    >
      <div className="text-[11px] text-tertiary font-medium tracking-[0.02em] mb-1.5">
        {label}
      </div>
      <div
        className={`text-2xl font-bold tracking-[-0.025em] leading-[1.1] tnum ${
          accent ? 'text-ok-deep' : 'text-primary'
        }`}
      >
        {value}
        {p !== null && (
          <span className="text-xs text-tertiary font-medium ml-1">· {p}%</span>
        )}
      </div>
    </div>
  );
}
